import { useState, type CSSProperties } from 'react';
import type { HypeKind } from '@racksavant/core';
import { cx } from '../cx';
import { CUTOUTS, FACES, PLATES, type CutoutId, type PlateId } from '../photos';
import s from './Photo.module.css';

/* =========================================================================
   Three ways a photograph appears on this site, and only three.

   PLATE   a rectangle. Runs to the edge, type sits on it, a credit line runs
           underneath. It resolves from grain rather than fading in: the
           overlay lifts and the halation clears, the way a print comes up.

   FACE    a die-cut cutout of a person reacting. This replaced the emoji
           chips. It is stuck on at an angle, it overlaps its neighbour, and
           it never sits inside a box.

   CUTOUT  the same treatment for a garment, floating free of any container.
   ========================================================================= */

interface PlateProps {
  id: PlateId;
  className?: string;
  /** Override the credit line, or pass `false` to run the frame silently. */
  caption?: string | false;
  /** Crop to a ratio (width / height) instead of the frame's natural one. */
  ratio?: number;
  /** Where the subject sits, for when a crop would otherwise cut a head off. */
  focus?: string;
  /** Above the fold: skip lazy-loading and decode eagerly. */
  eager?: boolean;
  style?: CSSProperties;
}

export function Plate({ id, className, caption, ratio, focus, eager, style }: PlateProps) {
  const plate = PLATES[id];
  const [loaded, setLoaded] = useState(false);
  const credit = caption === undefined ? plate.caption : caption;

  return (
    <figure className={cx(s.plate, className)} style={style}>
      <div
        className={s.frame}
        data-loaded={loaded ? 'true' : 'false'}
        style={{ aspectRatio: `${ratio ?? plate.ratio}` }}
      >
        <img
          className={s.img}
          src={plate.src}
          alt={plate.alt}
          /* A cached image can be complete before onLoad would ever fire. */
          ref={(node) => {
            if (node?.complete) setLoaded(true);
          }}
          loading={eager ? 'eager' : 'lazy'}
          decoding={eager ? 'sync' : 'async'}
          fetchPriority={eager ? 'high' : 'auto'}
          onLoad={() => setLoaded(true)}
          style={focus ? { objectPosition: focus } : undefined}
        />
        <span className={s.grain} aria-hidden="true" />
      </div>
      {credit ? <figcaption className={s.credit}>{credit}</figcaption> : null}
    </figure>
  );
}

interface FaceProps {
  kind: HypeKind;
  /** Rendered width in px. Faces are sized in absolutes, never in a grid cell. */
  size?: number;
  /** Degrees. One or two, occasionally four. Never enough to look like a toy. */
  tilt?: number;
  className?: string;
  style?: CSSProperties;
  /** Announce the reaction instead of hiding it — used where it is the content. */
  labelled?: boolean;
}

export function Face({ kind, size = 76, tilt = -3, className, style, labelled }: FaceProps) {
  const face = FACES[kind];
  return (
    <img
      className={cx(s.face, className)}
      src={face.src}
      alt={labelled ? face.alt : ''}
      aria-hidden={labelled ? undefined : true}
      loading="lazy"
      decoding="async"
      style={{ width: `${size}px`, rotate: `${tilt}deg`, ...style }}
    />
  );
}

interface CutoutProps {
  id: CutoutId;
  /** Rendered width in px, or any CSS length. */
  width?: number | string;
  tilt?: number;
  className?: string;
  style?: CSSProperties;
  /** Decorative by default — these float, they do not carry meaning. */
  labelled?: boolean;
}

export function Cutout({ id, width = 240, tilt = 2, className, style, labelled }: CutoutProps) {
  const cutout = CUTOUTS[id];
  return (
    <img
      className={cx(s.cutout, className)}
      src={cutout.src}
      alt={labelled ? cutout.alt : ''}
      aria-hidden={labelled ? undefined : true}
      loading="lazy"
      decoding="async"
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        rotate: `${tilt}deg`,
        ...style,
      }}
    />
  );
}
